import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/core/database/supabase';
import { workOrderEditService } from '../services/workOrderEdit.service';
import { DEMO_WORK_ORDERS } from '../../new/constants/workOrderDemo';

export function useWorkOrderEdit(id: string, companyId: string) {
  const queryClient = useQueryClient();

  const { data: workOrder, isLoading: isLoadingData } = useQuery({
    queryKey: ['work-order-edit', id],
    queryFn: async () => {
      const demo = DEMO_WORK_ORDERS.find(item => item.id === id);
      
      const { data, error } = await supabase
        .from('work_orders')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (data) {
        // Map any old maintenance fields back if they were stored differently
        data.assigned_team_id = data.assigned_team_id || data.team_id;
        data.planned_start_date = data.planned_start_date || data.scheduled_date;

        // Fetch members
        const { data: members } = await supabase
          .from('work_order_members')
          .select('user_id')
          .eq('work_order_id', id);
        
        data.collaborator_ids = (members || []).map(m => m.user_id);
      }

      if (error && !demo) throw error;
      return data || demo;
    },
    enabled: !!id,
  });

  const { data: referenceData, isLoading: isLoadingRef } = useQuery({
    queryKey: ['work-order-reference-data', companyId],
    queryFn: () => workOrderEditService.getReferenceData(companyId),
    enabled: !!companyId,
  });

  const updateMutation = useMutation({
    mutationFn: (values: any) => workOrderEditService.updateWorkOrder(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['work-order-detail', id] });
    }
  });

  return {
    workOrder,
    referenceData,
    isLoading: isLoadingData || isLoadingRef,
    isSaving: updateMutation.isPending,
    updateWorkOrder: updateMutation.mutateAsync,
  };
}
